import { useState } from 'react'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './LocationDeniedModal.module.css'

export interface LocationDeniedModalProps {
  /** Dismisses the modal. */
  readonly onClose: () => void
  /**
   * Best-effort "open settings" action.
   *
   * Browsers do **not** expose a programmatic deep-link to per-site
   * permission settings (researched: Chrome / Firefox / Safari all
   * gate location behind the user opening the site-info popover or the
   * browser settings UI manually — there is no `navigator` API for it).
   * So this handler's only reliable job is bookkeeping; the actual
   * remediation is the in-modal instruction panel shown below.
   */
  readonly onOpenSettings: () => void
}

/**
 * TASK-391 §3.4 location-denied modal (Web variant).
 *
 * Two actions — Close / Open Settings. Because no browser exposes a
 * deep-link to per-site location settings, tapping "Open Settings"
 * reveals an instruction panel telling the user how to re-enable
 * location for this site from their browser's site-info UI.
 */
export function LocationDeniedModal({
  onClose,
  onOpenSettings,
}: LocationDeniedModalProps): ReactElement {
  const { t } = useTranslation('common')
  const [showInstructions, setShowInstructions] = useState(false)

  const handleOpenSettings = (): void => {
    setShowInstructions(true)
    onOpenSettings()
  }

  return (
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label={t('location_denied.aria', { defaultValue: 'Location access denied' })}
    >
      <div className={styles.dialog}>
        <h2 className={styles.title}>
          {t('location_denied.title', { defaultValue: 'Location access is off' })}
        </h2>
        <p className={styles.body}>
          {t('location_denied.body', {
            defaultValue:
              'Metro needs your location to centre the map on your nearest station. Your browser is currently blocking location for this site.',
          })}
        </p>
        {showInstructions && (
          <ol className={styles.instructions} data-testid="location-denied-instructions">
            <li>
              {t('location_denied.step1', {
                defaultValue: "Tap the lock / site-info icon next to this page's address.",
              })}
            </li>
            <li>
              {t('location_denied.step2', { defaultValue: 'Find the "Location" permission.' })}
            </li>
            <li>
              {t('location_denied.step3', {
                defaultValue: 'Switch it to "Allow", then reload the page.',
              })}
            </li>
          </ol>
        )}
        <div className={styles.actions}>
          <button
            type="button"
            className={`${styles.button} ${styles.secondary}`}
            onClick={onClose}
          >
            {t('location_denied.close', { defaultValue: 'Close' })}
          </button>
          <button
            type="button"
            className={`${styles.button} ${styles.primary}`}
            onClick={handleOpenSettings}
          >
            {t('location_denied.open_settings', { defaultValue: 'Open Settings' })}
          </button>
        </div>
      </div>
    </div>
  )
}
